import PolicyLayout, { Section, P, Ul } from '../components/PolicyLayout';

export default function PrivacyPolicy() {
  return (
    <PolicyLayout art="privacy" title="Privacy policy" description="Understand how your information is collected, used and protected." updated="January 1, 2025">
        <Section title="1. Introduction">
          <P>
            Welcome to NephroCore ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and related services.
          </P>
          <P>
            Please read this policy carefully. If you disagree with its terms, please discontinue use of our application.
          </P>
        </Section>

        <Section title="2. Information We Collect">
          <P>We collect information that you provide directly to us, including:</P>
          <Ul items={[
            'Personal identification information (name, date of birth, gender)',
            'Contact information (email address, phone number)',
            'Health information (medical history, current conditions, symptoms, lab reports)',
            'Government ID details for identity verification (Aadhaar)',
            'Payment information processed securely through our payment partners',
            'Profile photos and documents you upload',
          ]} />
          <P>We also automatically collect certain information when you use our app:</P>
          <Ul items={[
            'Device information (device type, operating system, unique device identifiers)',
            'Usage data (features accessed, time spent, navigation patterns)',
            'Log data (IP address, crash reports, app performance data)',
            'Location information (only when you grant permission)',
          ]} />
        </Section>

        <Section title="3. How We Use Your Information">
          <P>We use the information we collect to:</P>
          <Ul items={[
            'Create and manage your account',
            'Facilitate lab test bookings and doctor consultations',
            'Process payments and manage transactions',
            'Send appointment reminders and health notifications',
            'Provide personalized health insights and recommendations',
            'Verify your identity and prevent fraud',
            'Improve our app features and user experience',
            'Comply with legal obligations and resolve disputes',
          ]} />
        </Section>

        <Section title="4. Sharing Your Information">
          <P>We may share your information with:</P>
          <Ul items={[
            'Healthcare providers (doctors, labs) you choose to consult or book with',
            'Payment processors to complete your transactions',
            'Identity verification partners to confirm your Aadhaar details',
            'Cloud service providers who host our infrastructure',
            'Analytics partners (in anonymized, aggregated form only)',
          ]} />
          <P>
            We do not sell your personal information to third parties. We only share your health data with the specific providers you interact with through our platform.
          </P>
        </Section>

        <Section title="5. Data Security">
          <P>
            We implement industry-standard security measures including end-to-end encryption for sensitive health data, secure HTTPS transmission, and restricted access controls. However, no method of electronic transmission or storage is 100% secure, and we cannot guarantee absolute security.
          </P>
        </Section>

        <Section title="6. Data Retention">
          <P>
            We retain your personal information for as long as your account is active or as needed to provide services. Health records are retained for a minimum of 7 years as required by applicable Indian healthcare regulations. You may request deletion of your account and non-mandated data by contacting us.
          </P>
        </Section>

        <Section title="7. Your Rights">
          <P>You have the right to:</P>
          <Ul items={[
            'Access the personal information we hold about you',
            'Correct inaccurate or incomplete information',
            'Request deletion of your account and personal data at nephrocorenexus.com/delete-account (subject to legal retention requirements)',
            'Opt out of marketing communications at any time',
            'Withdraw consent for non-essential data processing',
          ]} />
        </Section>

        <Section title="8. Children's Privacy">
          <P>
            Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from minors. If you believe we have inadvertently collected information from a minor, please contact us immediately.
          </P>
        </Section>

        <Section title="9. Changes to This Policy">
          <P>
            We may update this Privacy Policy periodically. We will notify you of significant changes via email or in-app notification. Your continued use of the app after changes become effective constitutes acceptance of the revised policy.
          </P>
        </Section>

        <Section title="10. Contact Us">
          <P>If you have questions or concerns about this Privacy Policy, please contact us at:</P>
          <div className="nc-policy-note">
            <p style={{ fontWeight: 700, color: 'var(--dark)', marginBottom: 4 }}>NephroCore</p>
            <a href="mailto:support@nephrocorenexus.com" style={{ color: 'var(--primary)', fontWeight: 600 }}>support@nephrocorenexus.com</a>
            <p style={{ color: 'var(--muted)', fontSize: '.9rem', marginTop: 4 }}>Mon–Sat, 10:00 AM – 6:00 PM IST</p>
          </div>
        </Section>
    </PolicyLayout>
  );
}
